'use client'

import { useState } from 'react'
import { Collapse } from '@/components/ui/Collapse'
import { getStaffByRole, getName } from '@/services/aroeven'
import { parsePhone } from '@/services/text'
import { status as s } from '@/services/status'
import { z } from 'zod'
import { messages } from '@/services/validation'
import { executeRecaptcha, useRecaptcha } from "@/services/recaptcha/client"
import Loading from 'app/loading'
import { createObjectActionPagePathname, createStaticPagePathname } from '@/services/pathname'
import Link from 'next/link'
import { postContactEducationCourse } from '@/api/helper'

type ContactProps = {
    aroeven: Aroeven,
    position?: string
    educationCourse: EducationCourse,
    proposedBy: Aroeven & AroevenStaffs
}

const ContactSchema = z.object({
    name: z.string().trim().min(1, messages.required).max(255, messages.tooLong),
    jobTitle: z.string().trim().min(1, messages.required).max(255, messages.tooLong),
    establishment: z.string().trim().min(1, messages.required).max(255, messages.tooLong),
    email: z.string().trim().min(1, messages.required).email(messages.email),
    phone: z.string().trim().min(1, messages.required).min(10, messages.tooShort).max(255, messages.tooLong),
    message: z.string().trim().min(1, messages.required).max(10000, messages.tooLong),
})

export default function Contact({
    aroeven,
    position = 'sidebar',
    educationCourse,
    proposedBy
}: ContactProps) {

    const [contactCollapseOpen, setContactCollapseOpen] = useState<boolean>(false)

    const [status, setStatus] = useState<string>(s.idle)
    useRecaptcha(contactCollapseOpen)

    const [errors, setErrors] = useState([])
    const [contact, setContact] = useState({
        name: '',
        jobTitle: '',
        establishment: '',
        email: '',
        phone: '',
        message: ''
    })

    async function postContact() {
        setErrors([])
        try {
            ContactSchema.parse(contact)
        }
        catch (error) {
            setErrors(error.issues)
            return
        }

        setStatus(s.loading)
        try {
            const token = await executeRecaptcha('contact_education_course')
            await postContactEducationCourse(educationCourse.proposedBy, {
                ...contact,
                object: educationCourse
            }, token)
            setStatus(s.finished)
        }
        catch (error) {
            console.log(error)
            setStatus(s.error)
            return
        }
    }

    return (
        <>
            {status === s.loading &&
                <Loading />
            }
            <div className={"encart-2 ml-lg-0 px-30 py-30 bg-bloc w-360" + (position === 'sidebar' ? ' d-none d-sm-block' : '')}>
                <div className="row y-gap-15 items-center justify-between">
                    <div className="col-auto">
                        <div className="text-14 text-light-1">
                            <h4>Organisé par :</h4>
                            <h4>
                                <a className="link">
                                    {getName(educationCourse.proposedBy)} <i className="fi fi-ss-angle-circle-right lh-15" />
                                </a>
                            </h4>
                        </div>
                    </div>
                    <div className="col-auto text-ocre-dark numbers text-18">
                        <i className="fi fi-rr-phone-flip" /> Contactez {getStaffByRole(proposedBy, 'education_course')?.firstName ?? 'l\'Aroéven'} au {parsePhone(getStaffByRole(proposedBy, 'education_course')?.phone ?? proposedBy.phone)}
                    </div>
                </div>
                <div className="separateur d-flex flex-row justify-content-between">
                    <div className="divider" />
                    <div>ou</div>
                    <div className="divider" />
                </div>
                <div className="row y-gap-20">
                    <div className="col-12">
                        <div className="col-12 mt-5">
                            <div className="accordion -simple js-accordion">
                                <div className="col-12">
                                    <div className={"accordion__item mb-15 " + (contactCollapseOpen === true ? 'is-active' : '')}>
                                        <a className="accordion__button button-primary demande mb-10 d-flex items-center justify-content-center" data-bs-toggle='collapse' href={'#e' + educationCourse.id + '-contact-collapse-content'} role="button">
                                            <div className="accordion__icon size-20 flex-center mr-10">
                                                <i className="fi fi-bs-arrow-small-down" />
                                                <i className="fi fi-bs-arrow-small-up" />
                                            </div>
                                            <div>
                                                Complétez la demande de contact
                                            </div>
                                        </a>
                                        <Collapse
                                            id={'e' + educationCourse.id + '-contact-collapse-content'}
                                            className='mt-10 y-gap-20'
                                            onShow={() => {
                                                setContactCollapseOpen(true)
                                            }}
                                            onHide={() => {
                                                setContactCollapseOpen(false)
                                            }}
                                        >
                                            {status === s.finished &&
                                                <>
                                                    <section className="texte">
                                                        <div className="container">
                                                            <div className="alert alert-success" role="alert">
                                                                <h4 className="alert-heading">Merci !</h4>
                                                                <p>Votre demande a bien été envoyée.</p>
                                                            </div>
                                                        </div>
                                                    </section>
                                                </>
                                            }
                                            {[s.idle, s.error].includes(status) === true &&
                                                <>
                                                    {status === s.error &&
                                                        <div className="alert alert-danger" role="alert">
                                                            <h4 className="alert-heading">Une erreur est survenue.</h4>
                                                            <p>Veuillez vérifier les données saisies dans le formulaire ou réessayer plus tard.</p>
                                                        </div>
                                                    }<div className="form-input">
                                                        <input type="text" className={errors.map(e => e.path[0]).indexOf('name') !== -1 ? 'requis' : ''} value={contact.name} onChange={(e) => setContact({
                                                            ...contact,
                                                            name: e.target.value
                                                        })} />
                                                        <label className="lh-1 text-16 text-light-1">
                                                            Nom <span className="obligatoire">*</span>
                                                        </label>
                                                    </div>
                                                    {errors.map(e => e.path[0]).indexOf('name') !== -1 && <div className="f-requis">{errors.filter(e => e.path[0] === 'name')[0].message}</div>}
                                                    <div className="form-input">
                                                        <input type="text" className={errors.map(e => e.path[0]).indexOf('jobTitle') !== -1 ? 'requis' : ''} value={contact.jobTitle} onChange={(e) => setContact({
                                                            ...contact,
                                                            jobTitle: e.target.value
                                                        })} />
                                                        <label className="lh-1 text-16 text-light-1">
                                                            Fonction <span className="obligatoire">*</span>
                                                        </label>
                                                    </div>
                                                    {errors.map(e => e.path[0]).indexOf('jobTitle') !== -1 && <div className="f-requis">{errors.filter(e => e.path[0] === 'jobTitle')[0].message}</div>}
                                                    <div className="form-input">
                                                        <input type="text" className={errors.map(e => e.path[0]).indexOf('establishment') !== -1 ? 'requis' : ''} value={contact.establishment} onChange={(e) => setContact({
                                                            ...contact,
                                                            establishment: e.target.value
                                                        })} />
                                                        <label className="lh-1 text-16 text-light-1">
                                                            Etablissement / structure <span className="obligatoire">*</span>
                                                        </label>
                                                    </div>
                                                    {errors.map(e => e.path[0]).indexOf('establishment') !== -1 && <div className="f-requis">{errors.filter(e => e.path[0] === 'establishment')[0].message}</div>}
                                                    <div className="form-input">
                                                        <input type="text" className={errors.map(e => e.path[0]).indexOf('email') !== -1 ? 'requis' : ''} value={contact.email} onChange={(e) => setContact({
                                                            ...contact,
                                                            email: e.target.value
                                                        })} />
                                                        <label className="lh-1 text-16 text-light-1">
                                                            Adresse email <span className="obligatoire">*</span>
                                                        </label>
                                                    </div>
                                                    {errors.map(e => e.path[0]).indexOf('email') !== -1 && <div className="f-requis">{errors.filter(e => e.path[0] === 'email')[0].message}</div>}
                                                    <div className="form-input">
                                                        <input type="text" className={errors.map(e => e.path[0]).indexOf('phone') !== -1 ? 'requis' : ''} value={contact.phone} onChange={(e) => setContact({
                                                            ...contact,
                                                            phone: e.target.value
                                                        })} />
                                                        <label className="lh-1 text-16 text-light-1">
                                                            Téléphone <span className="obligatoire">*</span>
                                                        </label>
                                                    </div>
                                                    {errors.map(e => e.path[0]).indexOf('phone') !== -1 && <div className="f-requis">{errors.filter(e => e.path[0] === 'phone')[0].message}</div>}
                                                    <div className="form-input">
                                                        <textarea className={errors.map(e => e.path[0]).indexOf('message') !== -1 ? 'requis' : ''} rows={4} value={contact.message} onChange={(e) => setContact({
                                                            ...contact,
                                                            message: e.target.value
                                                        })} />
                                                        <label className="lh-1 text-16 text-light-1">
                                                            Message <span className="obligatoire">*</span>
                                                        </label>
                                                    </div>
                                                    {errors.map(e => e.path[0]).indexOf('message') !== -1 && <div className="f-requis">{errors.filter(e => e.path[0] === 'message')[0].message}</div>}
                                                    <div className="form-input justify-content-end pt-0">
                                                        <button className="button-primary d-block" onClick={() => postContact()}>
                                                            Envoyer
                                                        </button>
                                                    </div>
                                                </>
                                            }
                                        </Collapse>
                                    </div>
                                </div>
                            </div>
                            {aroeven.federation !== true &&
                                <Link href={createStaticPagePathname('EducationCourse', 'Quotation', { target: educationCourse.id })} className="button-primary devis d-block w-100">
                                    Demander un devis
                                </Link>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}